import DeleteIcon from '@mui/icons-material/Delete';
import { Backdrop } from '@mui/material';
import React, { useState } from 'react';

import Arranger from '../base/arranger';
import Button from '../base/button';
import Modal from '../base/modal';
import { ProjectData } from '../dashboard/compliance';

import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

interface ModalContentProps {
  className?: string;
  entry: unknown;
  archiveProjectHandler: (id: string) => void;
  archiveProjectLoading: boolean;
  confirmText?: string;
  // Passing `open` makes the dialog controlled by the parent and hides the trash button.
  open?: boolean;
  onClose?: () => void;
}

const ModalComponent: React.FC<ModalContentProps> = ({
  className,
  archiveProjectHandler,
  entry,
  archiveProjectLoading,
  confirmText,
  open: controlledOpen,
  onClose,
}) => {
  const [open, setOpen] = useState<string | null>(null);
  const isControlled = controlledOpen !== undefined;
  const isProjectData = (data: unknown): data is ProjectData => {
    return (
      typeof data === 'object' &&
      data !== null &&
      'id' in data && // Check for specific properties
      'projectName' in data
    );
  };

  // narrow down the type
  const projectEntry = isProjectData(entry) ? entry : null;

  // Default text if no custom text is provided
  const defaultText = `Are you sure you want to delete project '${projectEntry?.projectName}'?`;
  const displayText = confirmText ?? defaultText;

  const close = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setOpen(null);
    }
  };

  return (
    <div
      className={className}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {!isControlled && (
        <Button
          onClick={() => {
            setOpen(projectEntry?.id ?? '');
          }}
          color="error"
          size="large"
          icon={DeleteIcon}
          loading={false}
          disabled={archiveProjectLoading}
        />
      )}
      <Modal
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(186, 187, 191, 0.4)',
            },
          },
        }}
        open={isControlled ? controlledOpen : open === projectEntry?.id}
        onClose={close}
      >
        <Arranger className={className} direction="column" justify="flex-start">
          <Text sz="bold-body16" textAlign="center">
            {displayText}
          </Text>
          <Spacer pb={spc.between_vertical_l} />

          <Arranger
            direction="row"
            justify="center"
            style={{ display: 'flex', gap: '10px' }}
          >
            <Button outlined onClick={close}>
              No
            </Button>
            <Button
              color="success"
              loading={archiveProjectLoading}
              disabled={archiveProjectLoading}
              onClick={() => {
                archiveProjectHandler(projectEntry?.id ?? '');
                close();
              }}
            >
              Yes
            </Button>
            <Spacer pb={spc.between_horizontal_s} />
          </Arranger>
          <Spacer pb={spc.container_padding_l} />
        </Arranger>
      </Modal>
    </div>
  );
};

export default ModalComponent;
